import { z } from "zod";
/**
 * The session credential pair (D15), as it appears on the wire.
 *
 * ── WHY snake_case IN A REPOSITORY THAT IS camelCase EVERYWHERE ──────────────
 * These five field names are the ones RFC 6749 §5.1 gives a token response.
 * Every other field in this package is camelCase and stays that way; the token
 * envelope is the one place a caller is likely to be a generic OAuth-shaped
 * client, or a native app's stock HTTP layer, rather than code written against
 * this API. Renaming them would be tidier and would silently break anything
 * written against the standard shape.
 *
 * ── THIS IS NOT A COOKIE, AND THAT IS THE POINT ──────────────────────────────
 * The response body carries both tokens and there is no `Set-Cookie` anywhere
 * in the auth flow. A browser attaches a cookie by itself and never attaches an
 * `Authorization` header by itself, which is what makes "no ambient credential
 * to ride" unconditionally true rather than route-dependent. Adding a cookie
 * back here reopens CSRF across the whole surface.
 */
export const tokenPairOutput = z.object({
    /** Compact HS256 JWS. Goes in `Authorization: Bearer <this>`. */
    access_token: z.string().min(1),
    /**
     * Unix **SECONDS** — mirrors the JWT `exp` claim so a client can schedule its
     * refresh without parsing the token. Milliseconds here would put every
     * scheduled refresh roughly 50,000 years out, and nothing would fail until
     * the access token quietly expired mid-session.
     */
    expires_at: z.number().int().positive(),
    /** Seconds until `expires_at`. 900 (D15), never configurable per environment. */
    expires_in: z.number().int().positive(),
    /**
     * Opaque, 256-bit CSPRNG, single-use and rotating (D15). Never a JWT: it is
     * the only revocable credential in the product, and revocability requires
     * that the server be consulted.
     */
    refresh_token: z.string().min(1),
    token_type: z.literal("Bearer"),
});
/**
 * The refresh request body.
 *
 * The refresh token travels in the BODY, not in `Authorization`. That header
 * carries the *access* token on every other route, and a route accepting either
 * in the same slot would eventually be handed the wrong one — with the failure
 * mode being family revocation, i.e. the user signed out of everything because
 * of someone else's bug.
 */
export const refreshTokenInput = z.object({
    refresh_token: z.string().min(1),
});
export const refreshTokenOutput = tokenPairOutput;
//# sourceMappingURL=token.js.map